/**
 * generatePropertyRecommendation — the thin FRONTEND callable seam for the L1A
 * per-property AI-assisted recommendation (Evaluate stage, mockup
 * 2026-07-01-l1a-streamlined-evaluate-mockup.html · §B AI panel).
 *
 * Invokes the backend callable `generatePropertyRecommendation` (Forge builds it
 * in PARALLEL — it may not be deployed yet) and types + normalises its response.
 * The callable synthesises a DRAFT recommendation from the already-live engine
 * findings + astro fit; the evaluator then edits the reasoning / changes the
 * status and CONFIRMS. The confirmed result persists to the EXISTING
 * `answers.properties[i].recommendation_status` + `recommendation_reasoning`
 * fields (staffIntake.ts) — NO new store, exactly like the per-property
 * recommendation editor already in StaffTechnicalPanel.
 *
 * Contract (Larry's relay, 2026-07-01):
 *   generatePropertyRecommendation({ projectId, propertyId }) →
 *     { propertyId,
 *       recommendation: 'recommended'|'recommended_with_remedies'
 *                       |'not_recommended'|'needs_review',
 *       reasoning: string,          // plain-language synthesis
 *       basis: { vastu: string[], astro: string|null },  // bullets it drew on
 *       confidence: 'high'|'medium'|'low',
 *       generatedAt: string, isDraft: true }
 *
 * EVERY field is nullable / defensive — a partial payload from an in-flight
 * backend contract must never crash the Evaluate screen. Same discipline as the
 * astro seam (astroVastu.ts). Region + emulator wiring inherited from the shared
 * `functions` client.
 *
 * ⚠️ CONTRACT SEAM to reconcile with Forge: the callable's `recommendation`
 * vocabulary carries an extra `'needs_review'` value that the persisted
 * `VerdictStatus` field does NOT have. `toVerdictStatus()` maps it to `''`
 * (— not assessed —) on persist, so the draft never writes an unknown enum into
 * `answers`. If Forge's final contract differs, only this file changes.
 */
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import { normaliseVerdictStatus, type VerdictStatus } from './staffIntake';

/* ════════════════════ Contract ════════════════════ */

/** The draft recommendation vocabulary from the callable (superset of the
 *  persisted `VerdictStatus` — adds `needs_review`). */
export type RecommendationDraftStatus =
  | 'recommended'
  | 'recommended_with_remedies'
  | 'not_recommended'
  | 'needs_review';

/** Confidence the engine attaches to its draft (display-only, for transparency). */
export type RecommendationConfidence = 'high' | 'medium' | 'low';

/** The bullet points the draft drew on — shown for transparency, never edited. */
export interface RecommendationBasis {
  /** Vastu findings the draft leaned on (grid / entrance / AUO). */
  vastu: string[];
  /** One-line astro-fit summary, or null when astro is not resolved. */
  astro: string | null;
}

/** A normalised, total, safe draft recommendation. */
export interface PropertyRecommendationDraft {
  propertyId: string;
  recommendation: RecommendationDraftStatus;
  /** Plain-language reasoning — the editable seed. */
  reasoning: string;
  basis: RecommendationBasis;
  confidence: RecommendationConfidence;
  /** ISO timestamp the backend computed the draft, or null. */
  generatedAt: string | null;
  /** Always true from the callable — a draft, never an authoritative write. */
  isDraft: boolean;
}

/* ════════════════════ The callable ════════════════════ */

/** The raw wire response — every field optional so a partial payload never
 *  trips a runtime error. The normaliser owns all coercion. */
interface RawRecommendationResponse {
  propertyId?: string | null;
  recommendation?: unknown;
  reasoning?: unknown;
  basis?: unknown;
  confidence?: unknown;
  generatedAt?: string | null;
  isDraft?: unknown;
}

const _generateRecommendation = httpsCallable<
  { projectId: string; propertyId: string },
  RawRecommendationResponse
>(functions, 'generatePropertyRecommendation');

/* ── defensive coercion helpers ── */

function str(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback;
}
function strOrNull(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null;
}
function isObj(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

function normalizeStatus(v: unknown): RecommendationDraftStatus {
  return v === 'recommended' ||
    v === 'recommended_with_remedies' ||
    v === 'not_recommended' ||
    v === 'needs_review'
    ? v
    : 'needs_review';
}

function normalizeConfidence(v: unknown): RecommendationConfidence {
  return v === 'high' || v === 'medium' || v === 'low' ? v : 'medium';
}

function normalizeBasis(v: unknown): RecommendationBasis {
  if (!isObj(v)) return { vastu: [], astro: null };
  const vastu = Array.isArray(v.vastu)
    ? v.vastu.map((x) => str(x)).filter((s) => s.length > 0)
    : [];
  return { vastu, astro: strOrNull(v.astro) };
}

/** Coerce any wire payload into a total, safe `PropertyRecommendationDraft`. */
export function normalizeRecommendationResponse(
  raw: RawRecommendationResponse | null | undefined,
  fallbackPropertyId = '',
): PropertyRecommendationDraft {
  const r = raw ?? {};
  return {
    propertyId: str(r.propertyId, fallbackPropertyId),
    recommendation: normalizeStatus(r.recommendation),
    reasoning: str(r.reasoning),
    basis: normalizeBasis(r.basis),
    confidence: normalizeConfidence(r.confidence),
    generatedAt: strOrNull(r.generatedAt),
    isDraft: r.isDraft !== false, // default true — it is always a draft
  };
}

/**
 * Map the draft vocabulary onto the persisted `VerdictStatus`. `needs_review`
 * (which the persisted field has no slot for) degrades to `''` (— not assessed
 * —), so confirming a `needs_review` draft never writes an unknown enum into
 * `answers.properties[i].recommendation_status`.
 */
export function toVerdictStatus(rec: RecommendationDraftStatus): VerdictStatus {
  return rec === 'needs_review' ? '' : normaliseVerdictStatus(rec);
}

/**
 * Generate the AI-assisted per-property recommendation draft. Returns `null` on
 * ANY failure (network, auth, backend error, or a NOT-YET-DEPLOYED callable) so
 * the Evaluate screen shows a calm "Generate" empty state instead of throwing.
 * Never rejects — identical discipline to `resolveAstroVastuForProject`.
 */
export async function generatePropertyRecommendation(args: {
  projectId: string;
  propertyId: string;
}): Promise<PropertyRecommendationDraft | null> {
  if (!args.projectId || !args.propertyId) return null;
  try {
    const result = await _generateRecommendation({
      projectId: args.projectId,
      propertyId: args.propertyId,
    });
    return normalizeRecommendationResponse(result.data, args.propertyId);
  } catch {
    // The callable may not be deployed yet (Forge is finalising it in parallel).
    // Degrade to null — the panel shows a "generate" empty state, never a crash.
    return null;
  }
}
